import type { FC } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  toast
} from '@cencosud-cencommerce/paris-uikit';
import { yupResolver } from '@hookform/resolvers/yup';

import { isDniRegisteredFeatureEnabled } from '@/config/featureFlags';
import type { AuthorizedPerson } from '@/modules/cart/domain/Cart';
import {
  type IdentificationDocumentType,
  IdentificationDocumentTypes
} from '@/modules/shared/types/IdentificationDocumentType';
import { useSetOnBehalfBuyNow } from '@/Pages/BuyNowPage/hooks/useSetOnBehalfBuyNow';
import {
  DEFAULT_FORM_VALUES,
  LABEL_CTA,
  LABEL_MODAL_TITLE,
  LABEL_REQUIRED_FIELDS,
  TOAST_ADD_PICKUP_PERSON_ERROR
} from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/constants';
import type {
  PickupPersonFormValues,
  PickupPersonModalProps
} from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/interfaces';
import PickupPersonFields from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/PickupPersonFields';
import pickupPersonValidator from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/pickupPersonValidator';
import { LocalHookFormDevtool } from '@/sections/shared/hooks/local-hookform-devtool';

export const PickupPersonModal: FC<PickupPersonModalProps> = (props) => {
  const { isPickupModalOpen, setIsPickupModalOpen, deliveryGroupId } = props;
  const { pickupId, setPickupId } = props;

  const { trigger: setOnBehalf, isLoading } = useSetOnBehalfBuyNow();

  const pickupPersonForm = useForm<PickupPersonFormValues>({
    resolver: yupResolver(pickupPersonValidator),
    defaultValues: DEFAULT_FORM_VALUES,
    values: {
      completeName: pickupId?.completeName || '',
      pickupPersonRut: pickupId?.pickupPersonRut || '',
      documentType: pickupId?.documentType || IdentificationDocumentTypes.RUT,
      documentID: pickupId?.documentID || ''
    }
  });

  if (!isPickupModalOpen) return null;

  const { control, handleSubmit, watch } = pickupPersonForm;

  const completeName = watch('completeName');
  const pickupPersonRut = watch('pickupPersonRut');
  const documentID = watch('documentID');
  const documentType = watch('documentType');

  const dniEnabled = isDniRegisteredFeatureEnabled();

  const isFormEmpty = dniEnabled
    ? !completeName || !documentID || !documentType
    : !completeName || !pickupPersonRut;

  const handleCTA: SubmitHandler<PickupPersonFormValues> = async (formData) => {
    const { completeName, pickupPersonRut, documentID, documentType } =
      formData;

    try {
      const authorizedPerson: AuthorizedPerson = {
        fullname: completeName
      };

      if (dniEnabled && documentType && documentID) {
        authorizedPerson.documentType =
          documentType as IdentificationDocumentType;
        authorizedPerson.documentID = documentID;
      } else {
        const rut = dniEnabled ? documentID : pickupPersonRut;
        authorizedPerson.rut = rut || '';
      }

      await setOnBehalf({
        deliveryGroupId,
        authorizedPerson
      });

      setIsPickupModalOpen(false);
      setPickupId({
        completeName: completeName,
        pickupPersonRut: documentID || pickupPersonRut,
        documentID,
        documentType
      });
    } catch (error) {
      toast(TOAST_ADD_PICKUP_PERSON_ERROR);
    }
  };

  const handleClose = () => {
    setIsPickupModalOpen(false);
  };

  return (
    <FormProvider {...pickupPersonForm}>
      <form onSubmit={handleSubmit(handleCTA)}>
        <Modal isOpen={isPickupModalOpen} onClose={handleClose} size="sm">
          <ModalHeader
            onClose={handleClose}
            title={LABEL_MODAL_TITLE}
            icon={{
              name: 'il-user',
              fill: 'primary-500'
            }}
          />
          <ModalBody className="co-p-6 co-m-0 co-pt-4">
            <div className="co-flex co-flex-col co-gap-2 co-pt-2">
              <Text
                className="co-flex co-justify-end"
                variant="caption"
                color="neutral-900"
              >
                {LABEL_REQUIRED_FIELDS}
              </Text>

              <PickupPersonFields />
            </div>
          </ModalBody>

          <ModalFooter className="co-justify-end">
            <Button
              size="xl"
              className="co-w-full tablet:co-w-auto"
              type="submit"
              loading={isLoading}
              disabled={isFormEmpty}
            >
              {LABEL_CTA}
            </Button>
          </ModalFooter>
        </Modal>

        <LocalHookFormDevtool control={control} />
      </form>
    </FormProvider>
  );
};
